import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { ArrowLeft, ArrowRight, X } from "lucide-react";
import { LessonContent } from "./LessonContent";
import { LessonIntroDialog } from "./LessonIntroDialog";
import { useLessonContent } from "@/hooks/use-lesson-content";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";

interface LessonModeProps {
  lesson: {
    id: string;
    title: string;
    description: string;
    content: string;
    duration: number;
    objectives?: string[];
  };
  onExit: () => void;
}

/**
 * Component that manages the full-screen lesson mode experience with quiz requirements
 * Users must pass the knowledge check to complete the lesson and earn points
 */
export const LessonMode = ({ lesson, onExit }: LessonModeProps) => {
  // State management for lesson progression and UI
  const [currentSection, setCurrentSection] = useState(0);
  const [showIntro, setShowIntro] = useState(true);
  const [isCompleted, setIsCompleted] = useState(false);
  const [quizPassed, setQuizPassed] = useState(false);
  
  // Query client for invalidating cache after completion
  const queryClient = useQueryClient();
  
  // Use the lesson content hook to parse the content properly
  const parsedContent = useLessonContent(lesson.content, lesson.objectives);
  
  // Extract content from the parsed structure with proper null safety
  const { sections: rawSections, objectives: rawObjectives, knowledgeChecks: rawKnowledgeChecks, rewards } = parsedContent;
  
  // Ensure we have safe arrays to work with - add null checks and default to empty arrays
  const sections = rawSections || [];
  const objectives = rawObjectives || [];
  const knowledgeChecks = rawKnowledgeChecks || [];
  const pointsToAward = rewards?.points || 50;
  
  console.log("📊 LessonMode using parsed content with null safety:", { 
    sectionsLength: sections.length, 
    objectivesLength: objectives.length, 
    knowledgeChecksLength: knowledgeChecks.length,
    pointsToAward,
    sectionsPreview: sections.map(s => ({ title: s?.title, textLength: s?.text?.length })),
    objectivesPreview: objectives,
    knowledgeChecksPreview: knowledgeChecks.map(kc => ({ question: kc?.question?.substring(0, 50) + "..." }))
  });
  
  // Calculate total sections including knowledge checks at the end - now safe from null errors
  const totalSections = sections.length + (knowledgeChecks.length > 0 ? 1 : 0);
  const progress = totalSections > 0 ? ((currentSection + 1) / totalSections) * 100 : 0;

  /**
   * Handle quiz completion - only proceed if quiz is passed
   */
  const handleQuizComplete = (passed: boolean) => {
    console.log("🎯 Quiz completion result:", passed);
    setQuizPassed(passed);
    if (passed) {
      console.log("✅ Quiz passed! User can now complete the lesson.");
    }
  };

  /**
   * Handle navigation through lesson sections and completion with points
   */
  const handleNext = async () => {
    console.log("➡️ Next button clicked - Current section:", currentSection, "Total sections:", totalSections);
    console.log("🎯 Quiz passed status:", quizPassed);
    
    // If we're at the quiz section and it's not passed, don't allow progression
    if (currentSection >= sections.length && knowledgeChecks.length > 0 && !quizPassed) {
      console.log("❌ Cannot proceed - quiz not passed");
      toast.warning("Complete the knowledge check correctly to finish the lesson");
      return;
    }

    if (currentSection < totalSections - 1) {
      console.log("📖 Moving to next section");
      setCurrentSection(prev => prev + 1);
    } else {
      // Mark lesson as completed and award points
      console.log("🎉 Attempting to complete lesson and award points");
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user) {
          console.log("👤 User authenticated:", session.user.id);
          console.log("💰 Points to award:", pointsToAward);
          
          // Check if progress already exists and is completed
          const { data: existingProgress, error: fetchError } = await supabase
            .from('user_progress')
            .select('*')
            .eq('user_id', session.user.id)
            .eq('lesson_id', lesson.id)
            .maybeSingle();

          if (fetchError) {
            console.error("❌ Error fetching existing progress:", fetchError);
          } else {
            console.log("📊 Existing progress:", existingProgress);
          }

          if (existingProgress?.status === 'completed') {
            console.log("⚠️ Lesson already completed, skipping points award");
            toast.info("You've already completed this lesson!");
            setIsCompleted(true);
            return;
          }

          // Update or insert progress record with points
          console.log("💾 Updating progress record...");
          const progressData = {
            user_id: session.user.id,
            lesson_id: lesson.id,
            status: 'completed',
            completed_at: new Date().toISOString(),
            points: parseInt(String(pointsToAward)) || 50 // Ensure points is a valid integer
          };
          
          console.log("📝 Progress data to upsert:", progressData);

          const { data: upsertData, error: progressError } = await supabase
            .from('user_progress')
            .upsert(progressData, {
              onConflict: 'user_id,lesson_id'
            })
            .select();

          if (progressError) {
            console.error("❌ Error updating lesson progress:", progressError);
            toast.error("Failed to update lesson progress. Please try again.");
            throw progressError;
          }

          console.log("✅ Successfully updated lesson progress:", upsertData);
          
          // Invalidate relevant queries to refresh the UI
          console.log("🔄 Invalidating queries to refresh UI...");
          await queryClient.invalidateQueries({ queryKey: ['userProgress'] });
          await queryClient.invalidateQueries({ queryKey: ['lessons'] });
          await queryClient.invalidateQueries({ queryKey: ['modules'] });
          
          setIsCompleted(true);
          toast.success(`🎉 Lesson completed! You earned ${pointsToAward} points!`, {
            description: "Keep up the great work!"
          });
        } else {
          console.error("❌ User not authenticated");
          toast.error("Please log in to save your progress");
        }
      } catch (error) {
        console.error("❌ Error in lesson completion process:", error);
        toast.error("Failed to complete lesson. Please try again.");
      }
    }
  };

  const handlePrevious = () => {
    if (currentSection > 0) {
      console.log("⬅️ Moving to previous section");
      setCurrentSection(prev => prev - 1);
    }
  };

  const handleComplete = () => {
    console.log("🏁 Exiting lesson mode");
    onExit();
  };

  // Handle empty content case with better messaging - now properly checks array length
  if (!sections || sections.length === 0) {
    return (
      <div className="fixed inset-0 bg-background z-[100] flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="mb-6">
            <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold mb-2">No content available</h2>
            <p className="text-gray-600 mb-4">
              This lesson doesn't have any content yet. Please try refreshing the page or contact support if this issue persists.
            </p>
            <p className="text-sm text-gray-500 mb-4">
              Lesson: {lesson.title} (ID: {lesson.id})
            </p>
          </div>
          <Button onClick={onExit}>Return to Lessons</Button>
        </div>
      </div>
    );
  }

  // Show completion screen with points earned
  if (isCompleted) {
    return (
      <div className="fixed inset-0 bg-background z-[100] flex items-center justify-center">
        <div className="text-center max-w-lg mx-auto px-4">
          <div className="mb-6">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold mb-2">Lesson Completed!</h2>
            <p className="text-gray-600 mb-2">Congratulations on completing {lesson.title}!</p>
            <p className="text-lg font-semibold text-blue-600">+{pointsToAward} points earned!</p>
          </div>
          <Button onClick={handleComplete}>Continue</Button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="fixed inset-0 bg-background z-[100] flex flex-col h-[100dvh]" data-lesson-mode="true">
      {/* Top Navigation Bar */}
      <div className="h-14 md:h-16 border-b bg-background/80 backdrop-blur-sm flex items-center justify-between px-4 md:px-6 sticky top-0">
        <div className="flex items-center gap-3 md:gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={onExit}
            className="hover:bg-red-50 hover:text-red-600 transition-colors"
          >
            <X className="h-5 w-5" />
          </Button>
          <div className="text-base md:text-lg font-semibold truncate max-w-[200px] md:max-w-none">
            {lesson.title}
          </div>
        </div>
        <Progress value={progress} className="w-32 md:w-48" />
      </div>

      {/* Main Content Area */}
      <div className="flex-1 overflow-y-auto px-4 md:px-6 py-6 md:py-8 max-w-4xl mx-auto w-full">
        {showIntro ? (
          <LessonIntroDialog
            title={lesson.title}
            objectives={objectives}
            duration={lesson.duration}
            points={pointsToAward}
            onComplete={() => setShowIntro(false)}
          />
        ) : (
          <div className="space-y-6 md:space-y-8 animate-fade-in">
            <LessonContent
              sections={sections}
              currentSection={currentSection}
              knowledgeChecks={knowledgeChecks}
              onQuizComplete={handleQuizComplete}
            />
          </div>
        )}
      </div>

      {/* Bottom Navigation */}
      {!showIntro && (
        <div className="h-16 md:h-20 border-t bg-background/80 backdrop-blur-sm flex items-center justify-between px-4 md:px-6 sticky bottom-0">
          <Button
            variant="ghost"
            onClick={handlePrevious}
            disabled={currentSection === 0}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="hidden md:inline">Previous</span>
          </Button>
          
          <Button
            onClick={handleNext}
            disabled={currentSection >= sections.length && knowledgeChecks.length > 0 && !quizPassed}
            className="flex items-center gap-2"
          >
            <span className="hidden md:inline">
              {currentSection === totalSections - 1 ? 'Complete' : 'Next'}
            </span>
            <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
};
